
import common from "@/common";
import AjaxCache from "@/interface/api/common/AjaxCache";
import accountState from "@/state/AccountState";
import nortification, { NORTIFICATION_NOT_LOGIN } from "@/state/NortificationState";

export default class {

  get = (url: string, queryString?: string, useCache = true): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.getAPI(url, queryString, useCache, this.getSetting()));
  }

  post = (url: string, queryString?: string, body?: any): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, queryString, this.jsonSetting("POST", body)));
  }

  postForm = (url: string, body: FormData): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, undefined, this.formDataSetting("POST", body)));
  }

  postImage = (url: string, queryString?: string, body?: any): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, queryString, this.jsonSetting("POST", body)));
  }

  put = (url: string, queryString?: string): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, queryString, this.jsonSetting("PUT")));
  }

  putForm = (url: string, body: FormData): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, undefined, this.formDataSetting("PUT", body)));
  }

  patch = (url: string, queryString?: string, body?: any): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, queryString, this.jsonSetting("PATCH", body)));
  }

  patchFormData = (url: string, formData: FormData): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, undefined, this.formDataSetting("PATCH", formData)));
  }

  delete = (url: string, queryString?: string): Promise<Response> => {
    return this.startOnCSRFLoaded(()=> this.requestAPI(url, queryString, this.jsonSetting("DELETE")));
  }

  // delete2 = (url: string, queryString?: string): Promise<Response> => {
  //   return this.requestAPI(url, queryString, this.deleteSetting2());
  // }

  private getSetting = () => {
    const method = "GET";
    const ret = { method } as RequestInit;
    this.addCredentials(ret);
    return ret;
  }

  private jsonSetting = (method: "POST" | "PUT" | "PATCH" | "DELETE" ,body?: any) => {
    const headers = this.createPostHeader();
    const ret = { method, headers } as RequestInit;
    if(body){
      ret.body = JSON.stringify(body)
    }
    this.addCredentials(ret);
    return ret;
  }

  // private deleteSetting2 = () => {
  //   const method = "DELETE";
  //   const headers = this.createPostHeader();
  //   (<any>headers)["redirect"] = "error";
  //   const ret = { method, headers } as RequestInit;
  //   this.addCredentials(ret);
  //   return ret;
  // }

  private formDataSetting = (method: "POST" | "PUT" | "PATCH" | "DELETE" , formData: FormData) => {
    const headers = this.createFormHeader();
    const ret = { method, headers, body: formData } as RequestInit;
    this.addCredentials(ret);
    return ret
  }

  private addCredentials(init: RequestInit) {
    //Session情報を含める。Vue-cliで動作させる時は、CROS（Spring boot）サーバーにSession情報を送るように"include"にする。
    //include: Always include credentials, even for cross-origin requests.
    //same-origin: Only send and include credentials for same-origin requests.
    init["credentials"] = process.env.VUE_APP_VUE_CLI ? "include" : "same-origin";
  }

  private createPostHeader = (type = "application/json", _length = 0) => {
    const ret = {
      Accept: "application/json",
      "Content-type": type,
      // "Content-Length": length, //サロゲートペア問題とかもあるので、一旦放置。
    } as any;
    
    this.addCSRFToken(ret)
    return ret;
  }
  private createFormHeader = () => {
    const ret = {} as any;
    this.addCSRFToken(ret)
    return ret;
  }

  private addCSRFToken = (header:any) => {
    if(window.csrfToken){
			//CSRFトークンはHTML+HTTP Headerで渡す。
      //Cookieで渡すと、npmパッケージに悪意があると取れたり、取れなくても送信できるので、HTMLで渡す方法よりセキュリティリスクがあると思われる。
      header[window.csrfHeader] = window.csrfToken
    }
  }

  private startOnCSRFLoaded(func:()=>Promise<Response>){
    return new Promise<Response>((resolve, reject) => {
      //CSRFトークンが取得できるようになってから実行する（本番環境ではCSRFトークン設定のJavaScriptがHTMLの並び順で下になるため）
      const start = ()=>{
        if(window.csrfToken === null){
          //csrfToken === nullの場合は、Spring boot側でのcsrfToken設定途中でエラー。
          //todo:たぶんそうそうないので適当にエラー表示する。
          console.error("SERVER ERROR");
          reject(new Error("SERVER ERROR"));

        }else if(window.csrfToken || process.env.VUE_APP_VUE_CLI){
          resolve(func());
        }else{
          common.runInNextTick(start)
        }
      }
      start()
    })
  }

  private getAPI = (url: string, queryString?: string, useCache?: boolean, init?: RequestInit): Promise<Response> => {
    if (window.ajaxCache && window.ajaxCache[url] !== undefined) {
      const ajaxCache = window.ajaxCache[url] as AjaxCache;
      //使用するのは同じURLに対して１回のみとする
      window.ajaxCache[url] = undefined as any;

      if (useCache) {
        if (process.env.VUE_APP_MODE === "development")console.log("GET APIのキャッシュを使用します URL:%o, response:%o", url, ajaxCache);
        return new Promise<Response>((resolve) => {
          //通信したことにして、ダミーのResponseを返す。
          resolve({
            ok: ajaxCache.status === 200,
            status: ajaxCache.status,
            json: () => {
              return new Promise((resolve) => resolve(ajaxCache.body));
            }
          } as Response);
        });
      }
    }

    return this.requestAPI(url, queryString, init);
  }

  private requestAPI = (url: string, queryString?: string, init?: RequestInit, tryCount = 0): Promise<Response> => {
    const ret = fetch(queryString ? url + "?" + queryString : url, init)
      //レスポンスがない場合のエラーだけを処理したいので、thenより先にcatchを追加する。（thenを先に持ってくるとそのthenで発生した例外はその後のcatchの対象になる）
      .catch((error: any) => {
        if (process.env.VUE_APP_MODE === "development") {
          console.log("通信エラー(%d)。サーバーが起動しているか確認してください。\nサーバー起動済でエラーが不明の場合、application-local.ymlの'[org.springframework.security]'をtraceにしてください。\nurl:%o ,queryString:%o\ninit:%o\n", tryCount, url, queryString, init, error);


        }

        return new Promise<any>((resolve, reject) => {
          if(tryCount < 3){
            setTimeout(() => {
              resolve(this.requestAPI(url, queryString, init, ++tryCount));
            }, tryCount * tryCount * 300 + 500)
          }else{
            console.error("通信エラーが発生しました。", error);
            reject(error)
          }
        });
      });

    if (tryCount === 0) {
      return ret.then(this.whenHasResponse);
    } else {
      return ret;
    }
  }

  private whenHasResponse = (response: Response): Response => {
    if (response.ok) {
      // console.log("fetch response:", response);
    } else {
      //レスポンスがFORBIDDENの場合の共通処理
      if(response.status === 403){
        //既にログイン済みの場合
        if(accountState.isLogin){
          //ログインが切れた場合、勝手にリロードするとユーザの作業内容が失われる可能性があるので警告のみする
          nortification.add({identifer:NORTIFICATION_NOT_LOGIN, type:"error", message:"ログイン状態が切れています。入力途中の内容をバックアップして再ログインしてください。"})
        }

        if (process.env.VUE_APP_MODE === "development") {
          console.log("【403エラーについて】ログインSessionが無いか間違っている、もしくはCSRFトークンがない場合があります")
        }
        
      }
      if (response.status >= 500) {
        if (process.env.VUE_APP_MODE === "development") {
          console.log("想定外のエラー。URLが間違ってないか確認してください。", response);
        }
        console.error("error status:", response.status);
        response.text().then((text) => {
          console.error("error body:", text);
        });
        alert("申し訳ありません。サーバーエラーが発生し処理完了できませんでした。")
      }
    }
    return response;
  }
}